import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from './api.service';
import { PaginaPdfResponse } from './dettaglio.service';

// G04 — risposta multi-pagina verso LIBRA
export interface PaginePdfResponse {
  message?: { code: string; message: string; type: string };
  pagine: PaginaPdfResponse[];
}

export interface TestataElenco {
  idTestata: number;
  nomeTestata: string;
  cartellaTestata: string;
  codTema?: number;
  annoFondazione?: number;
  siglaProv?: string;
  comune?: string;
  stato?: number;
  tema?: string;
  periodoFreq?: string;
  latitudine?: number;
  longitudine?: number;
}

export interface ElencoTestateResponse {
  message?: { code: string; message: string; type: string };
  data: TestataElenco[];
}

export interface Testata {
  idGdpTestata?: number;
  idGdpFascicolo?: number;
  nomeTestata: string;
  dataUltimaEdizione: string;
  invioEdizione?: boolean;
  provincia?: string;
  socEditrice?: string;
  soc_editrice?: string;
  stato?: string;
  comune?: string;
  [key: string]: any;
}

export interface GetLatestTestateResponse {
  data: TestataElenco[];
}

// A02 — FascicoloCheck
export interface FascicoloCheckPage {
  idPagina: number;
  posizione: number;
  numeroPagina?: number | null;
  notePagina?: string | null;
  nomeTestata?: string | null;
  siglaProv?: string | null;
  comune?: string | null;
  dataEdizione?: string | null;
}

export interface FascicoloCheckDetail {
  idFascicolo: number; // 0 se non esiste
  titolo?: string | null;
  pagFascicolo: FascicoloCheckPage[];
}

export interface FascicoloCheckResponse {
  message?: { code: string; message: string; type: string };
  data: FascicoloCheckDetail;
}

// A03 — FascicoloAdd
export interface FascicoloAddRequest {
  idFascicolo?: number;
  idPagina: number;
}

export interface FascicoloAddResponse {
  message?: { code: string; message: string; type: string };
  data: { idFascicolo: number };
}

// A04 — FascicoloDelete
export interface FascicoloDeleteRequest {
  idFascicolo: number;
  idPagina?: number;
  idTestata?: number;
}

export interface FascicoloDeleteResponse {
  message?: { code: string; message: string; type: string };
  data: { numPagine: number };
}

export interface PdfResponse {
  message?: { code: string; message: string; type: string };
  data: string; // Base64-encoded PDF
}

@Injectable({
  providedIn: 'root',
})
export class FascicoloService {
  apiService = inject(ApiService);

  getLatestTestate(
    areaTema: 'ALL' | 'G' | 'T' = 'ALL',
  ): Observable<ElencoTestateResponse> {
    return this.apiService.get<ElencoTestateResponse>(`/fo/testate`, { areaTema });
  }

  // A05 — Home page counters
  getConteggiHome(): Observable<any> {
    return this.apiService.get<any>(`/fo/home/conteggi`);
  }

  // A02 — Recupera il fascicolo dell'utente con le pagine salvate
  // Ora supporta parametri facoltativi di paginazione `page` e `pageSize`.
  checkFascicolo(page?: number, pageSize?: number): Observable<FascicoloCheckResponse> {
    const params: Record<string, any> = {};
    if (page !== undefined && page !== null) params['page'] = page;
    if (pageSize !== undefined && pageSize !== null) params['pageSize'] = pageSize;
    return this.apiService.get<FascicoloCheckResponse>(`/fo/fascicoli/check`, params);
  }

  // A03 — Aggiunge una pagina al fascicolo
  addToFascicolo(request: FascicoloAddRequest): Observable<FascicoloAddResponse> {
    return this.apiService.post<FascicoloAddResponse>(`/fo/fascicoli/add`, request);
  }

  // A04 — Elimina il fascicolo intero o una singola pagina
  deleteFromFascicolo(request: FascicoloDeleteRequest): Observable<FascicoloDeleteResponse> {
    return this.apiService.post<FascicoloDeleteResponse>(`/fo/fascicoli/delete`, request);
  }

  // G04 — Scarica il PDF di un insieme di pagine (ciascuna con proprio base64 + metadati)
  getPaginePdf(idPagina: number[]): Observable<PaginePdfResponse> {
    return this.apiService.get<PaginePdfResponse>(`/fo/pagine/pdf`, { idPagina });
  }

  // G04 — Scarica il PDF di una singola pagina (wrapper di comodo su getPaginePdf)
  downloadPaginaPdf(idPagina: number): Observable<PaginaPdfResponse> {
    return this.getPaginePdf([idPagina]).pipe(
      map((response) => {
        const pagina = response.pagine?.[0];
        if (!pagina) {
          throw new Error('Pagina non trovata nella risposta');
        }
        return pagina;
      }),
    );
  }

  getEdizionePdf(idEdizione: number | string): Observable<PdfResponse> {
    return this.apiService.get<PdfResponse>(`/fo/edizioni/${idEdizione}/pdf`);
  }
}
